import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

/**
 * Parse a response defensively. A proxy timeout or any HTML error page must
 * surface as a readable message rather than "Unexpected token '<'".
 */
async function readJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch (e) {
    const snippet = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
    throw new Error(
      `Server returned ${res.status} ${res.statusText || ''} (non-JSON response)` +
      (snippet ? `: ${snippet}` : '')
    );
  }
}

const CLIMATE_VARIABLES = [
  { id: 'ghi', label: 'GHI (Global Horizontal Irradiance)', unit: 'W/m\u00B2', category: 'Solar' },
  { id: 'dni', label: 'DNI (Direct Normal Irradiance)', unit: 'W/m\u00B2', category: 'Solar' },
  { id: 'dhi', label: 'DHI (Diffuse Horizontal Irradiance)', unit: 'W/m\u00B2', category: 'Solar' },
  { id: 'temperature', label: 'Temperature (2m)', unit: '\u00B0C', category: 'Meteorological' },
  { id: 'wind_speed', label: 'Wind Speed (10m)', unit: 'm/s', category: 'Meteorological' },
  { id: 'humidity', label: 'Relative Humidity (2m)', unit: '%', category: 'Meteorological' },
  { id: 'pressure', label: 'Surface Pressure', unit: 'hPa', category: 'Meteorological' },
  { id: 'precipitation', label: 'Precipitation', unit: 'mm', category: 'Meteorological' },
];

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles = {
  container: {
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    borderRadius: 8,
    padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  heading: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 20,
    color: '#f1f5f9',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 12,
    color: '#f1f5f9',
    borderBottom: '1px solid #334155',
    paddingBottom: 8,
  },
  section: {
    marginBottom: 28,
  },
  card: {
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 6,
    padding: 16,
    marginBottom: 12,
  },
  label: {
    fontSize: 12,
    color: '#94a3b8',
    marginBottom: 4,
    display: 'block',
  },
  value: {
    fontSize: 14,
    color: '#e2e8f0',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  input: {
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 4,
    color: '#e2e8f0',
    padding: '6px 10px',
    fontSize: 13,
    width: 80,
  },
  button: {
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  buttonDisabled: {
    backgroundColor: '#334155',
    color: '#64748b',
    border: 'none',
    borderRadius: 4,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  buttonSmall: {
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '5px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  },
  checkmark: {
    color: '#22c55e',
    fontWeight: 700,
    marginRight: 6,
  },
  missing: {
    color: '#f87171',
    fontWeight: 600,
    marginRight: 6,
  },
  tag: {
    display: 'inline-block',
    backgroundColor: '#334155',
    color: '#cbd5e1',
    borderRadius: 4,
    padding: '2px 8px',
    fontSize: 11,
    marginRight: 4,
    marginBottom: 4,
  },
  sourceColor: (color) => ({
    display: 'inline-block',
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: color || '#64748b',
    marginRight: 6,
  }),
  loadingOverlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    color: '#94a3b8',
    fontSize: 14,
  },
  errorText: {
    color: '#f87171',
    fontSize: 13,
    marginTop: 6,
  },
  successText: {
    color: '#22c55e',
    fontSize: 13,
    marginTop: 6,
  },
  checkbox: {
    accentColor: '#3b82f6',
    marginRight: 6,
  },
  variableRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid #1e293b',
  },
};

// ---------------------------------------------------------------------------
// AddDataPanel
// ---------------------------------------------------------------------------
const AddDataPanel = ({ locationId, latitude, longitude, onDataAdded, focusSourceId, refreshKey }) => {
  // Data state
  const [inventory, setInventory] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extend state: per-dataset extend controls
  const [extendState, setExtendState] = useState({});
  // { [datasetId]: { startYear, endYear, busy, msg } }

  // New source state
  const [newSourceSelections, setNewSourceSelections] = useState({});
  // { [sourceId]: { variables: Set, startYear, endYear } }
  const [downloadingSource, setDownloadingSource] = useState(null);
  const [downloadMsg, setDownloadMsg] = useState('');
  const [downloadMsgSource, setDownloadMsgSource] = useState(null);

  // Variable gap state
  const [gapBusy, setGapBusy] = useState(null); // variable id currently being added
  const [gapMsg, setGapMsg] = useState('');

  // -----------------------------------------------------------------------
  // Fetch inventory + availability
  // -----------------------------------------------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [invRes, availRes] = await Promise.all([
        fetch(`${API}/api/locations/${locationId}/inventory`),
        fetch(`${API}/api/sources/availability?lat=${latitude}&lon=${longitude}`),
      ]);
      if (!invRes.ok) throw new Error(`Inventory fetch failed (${invRes.status})`);
      if (!availRes.ok) throw new Error(`Availability fetch failed (${availRes.status})`);
      const invData = await invRes.json();
      const availData = await availRes.json();
      setInventory(invData);
      setAvailability(availData);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [locationId, latitude, longitude]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Arriving from a map-marker click: preselect that source's variables and
  // scroll its card into view, so the click actually lands somewhere useful.
  const focusRef = React.useRef(null);
  useEffect(() => {
    if (!focusSourceId || !availability) return;
    const src = availability.sources.find((s) => s.id === focusSourceId);
    if (!src) return;
    setNewSourceSelections((prev) => {
      if (prev[focusSourceId]) return prev;   // don't clobber a live edit
      return {
        ...prev,
        [focusSourceId]: {
          variables: new Set(src.variables || []),
          startYear: '',
          endYear: '',
        },
      };
    });
    if (focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusSourceId, availability]);

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  /** Find a source in availability by id */
  const findSource = (sourceId) => {
    if (!availability) return null;
    return availability.sources.find((s) => s.id === sourceId);
  };

  /** Get all source IDs already used by any dataset */
  const usedSourceIds = () => {
    if (!inventory) return new Set();
    const ids = new Set();
    for (const ds of inventory.datasets) {
      // source field may be the primary source id
      if (ds.source) ids.add(ds.source);
      // variable_sources maps variable -> sourceId
      if (ds.variable_sources) {
        Object.values(ds.variable_sources).forEach((sid) => ids.add(sid));
      }
    }
    return ids;
  };

  /** Get all variables currently covered across all datasets */
  const coveredVariables = () => {
    if (!inventory) return {};
    const covered = {}; // { variableId: sourceName }
    for (const ds of inventory.datasets) {
      if (ds.variable_completeness) {
        for (const varId of Object.keys(ds.variable_completeness)) {
          if (!covered[varId]) {
            const srcId = ds.variable_sources?.[varId] || ds.source;
            const src = findSource(srcId);
            covered[varId] = src ? src.name : srcId;
          }
        }
      }
    }
    return covered;
  };

  /** Find which available sources can provide a given variable */
  const sourcesForVariable = (varId) => {
    if (!availability) return [];
    return availability.sources.filter(
      (s) => s.variables && s.variables.includes(varId) && s.integrated !== false
    );
  };

  // -----------------------------------------------------------------------
  // Section 1: Extend Existing Dataset
  // -----------------------------------------------------------------------
  const getExtendableDatasets = () => {
    if (!inventory || !availability) return [];
    return inventory.datasets
      .filter((ds) => ds.status === 'complete')
      .map((ds) => {
        // Find the best available year_max for this dataset's sources
        const sourceIds = ds.variable_sources
          ? [...new Set(Object.values(ds.variable_sources))]
          : [ds.source];
        let availableMax = ds.year_end;
        let availableMin = ds.year_start;
        const sourceNames = [];
        for (const sid of sourceIds) {
          const src = findSource(sid);
          if (src) {
            if (src.year_max && src.year_max > availableMax) availableMax = src.year_max;
            if (src.year_min && src.year_min < availableMin) availableMin = src.year_min;
            sourceNames.push(src.name || sid);
          } else {
            sourceNames.push(sid);
          }
        }
        const canExtend = availableMax > ds.year_end || availableMin < ds.year_start;
        return { ...ds, availableMax, availableMin, canExtend, sourceNames };
      });
  };

  const handleExtendChange = (dsId, field, value) => {
    setExtendState((prev) => ({
      ...prev,
      [dsId]: { ...prev[dsId], [field]: value },
    }));
  };

  const handleExtend = async (ds) => {
    const state = extendState[ds.id] || {};
    const startYear = parseInt(state.startYear || ds.year_end + 1, 10);
    const endYear = parseInt(state.endYear || ds.availableMax, 10);
    if (isNaN(startYear) || isNaN(endYear) || startYear > endYear) return;

    setExtendState((prev) => ({
      ...prev,
      [ds.id]: { ...prev[ds.id], busy: true, msg: '' },
    }));

    try {
      const res = await fetch(
        `${API}/api/locations/${locationId}/datasets/${ds.id}/extend`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ startyear: startYear, endyear: endYear }),
        }
      );
      const json = await readJson(res);
      if (res.ok) {
        setExtendState((prev) => ({
          ...prev,
          [ds.id]: {
            ...prev[ds.id],
            busy: false,
            msg: `Extended: ${json.rows_inserted?.toLocaleString() || 0} records added`,
          },
        }));
        onDataAdded && onDataAdded();
        fetchData();
      } else {
        setExtendState((prev) => ({
          ...prev,
          [ds.id]: { ...prev[ds.id], busy: false, msg: `Error: ${json.error}` },
        }));
      }
    } catch (e) {
      setExtendState((prev) => ({
        ...prev,
        [ds.id]: { ...prev[ds.id], busy: false, msg: `Error: ${e.message}` },
      }));
    }
  };

  // -----------------------------------------------------------------------
  // Section 2: Add New Source
  // -----------------------------------------------------------------------
  /**
   * Every source we can actually download from, flagged with whether this
   * location already holds data for it. Previously this hid used sources
   * entirely, which meant clicking a stored source's marker led to an empty
   * panel and there was no way to re-download a different year range.
   */
  const getDownloadableSources = () => {
    if (!availability) return [];
    const used = usedSourceIds();
    return availability.sources
      .filter((s) => s.integrated !== false && s.availability_status !== 'not_integrated')
      .map((s) => ({ ...s, inUse: used.has(s.id) }))
      .sort((a, b) => (a.inUse === b.inUse ? 0 : a.inUse ? 1 : -1));
  };

  const selectAllVariables = (source, all) => {
    setNewSourceSelections((prev) => {
      const current = prev[source.id] || { variables: new Set(), startYear: '', endYear: '' };
      return {
        ...prev,
        [source.id]: {
          ...current,
          variables: all ? new Set(source.variables || []) : new Set(),
        },
      };
    });
  };

  const toggleNewSourceVariable = (sourceId, varId) => {
    setNewSourceSelections((prev) => {
      const current = prev[sourceId] || { variables: new Set(), startYear: '', endYear: '' };
      const vars = new Set(current.variables);
      if (vars.has(varId)) vars.delete(varId);
      else vars.add(varId);
      return { ...prev, [sourceId]: { ...current, variables: vars } };
    });
  };

  const handleNewSourceYearChange = (sourceId, field, value) => {
    setNewSourceSelections((prev) => {
      const current = prev[sourceId] || { variables: new Set(), startYear: '', endYear: '' };
      return { ...prev, [sourceId]: { ...current, [field]: value } };
    });
  };

  const handleDownloadNewSource = async (source) => {
    const sel = newSourceSelections[source.id];
    if (!sel || sel.variables.size === 0) return;

    const startYear = parseInt(sel.startYear || source.year_min, 10);
    const endYear = parseInt(sel.endYear || source.year_max, 10);
    if (isNaN(startYear) || isNaN(endYear) || startYear > endYear) return;

    // Build variable_sources map: each selected variable -> this source
    const variableSources = {};
    for (const v of sel.variables) {
      variableSources[v] = source.id;
    }

    setDownloadingSource(source.id);
    setDownloadMsgSource(source.id);
    setDownloadMsg(
      `Fetching ${endYear - startYear + 1} year(s) from ${source.name}\u2026 ` +
      `this runs synchronously and can take several minutes for long ranges.`
    );

    try {
      const res = await fetch(`${API}/api/locations/${locationId}/download/mixed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variable_sources: variableSources,
          startyear: startYear,
          endyear: endYear,
        }),
      });
      const json = await readJson(res);
      if (res.ok) {
        setDownloadMsg(
          `Downloaded ${json.rows_inserted?.toLocaleString() || 0} records ` +
          `(${json.variables?.join(', ') || '...'}) for ${startYear}\u2013${endYear}`
        );
        onDataAdded && onDataAdded();
        fetchData();
      } else {
        setDownloadMsg(`Error: ${json.error}`);
      }
    } catch (e) {
      setDownloadMsg(`Error: ${e.message}`);
    } finally {
      setDownloadingSource(null);
    }
  };

  // -----------------------------------------------------------------------
  // Section 3: Variable Gaps - Add missing variable
  // -----------------------------------------------------------------------
  const handleAddVariable = async (varId, sourceId) => {
    const src = findSource(sourceId);
    if (!src) return;

    const startYear = src.year_min || 2005;
    const endYear = src.year_max || 2024;

    const variableSources = { [varId]: sourceId };

    setGapBusy(varId);
    setGapMsg('');

    try {
      const res = await fetch(`${API}/api/locations/${locationId}/download/mixed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variable_sources: variableSources,
          startyear: startYear,
          endyear: endYear,
        }),
      });
      const json = await readJson(res);
      if (res.ok) {
        setGapMsg(`Added ${varId}: ${json.rows_inserted?.toLocaleString() || 0} records`);
        onDataAdded && onDataAdded();
        fetchData();
      } else {
        setGapMsg(`Error: ${json.error}`);
      }
    } catch (e) {
      setGapMsg(`Error: ${e.message}`);
    } finally {
      setGapBusy(null);
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingOverlay}>Loading inventory and source availability...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.heading}>Add Data</div>
        <div style={styles.errorText}>{error}</div>
      </div>
    );
  }

  const extendableDatasets = getExtendableDatasets();
  const downloadableSources = getDownloadableSources();
  const covered = coveredVariables();

  return (
    <div style={styles.container}>
      <div style={styles.heading}>Add Data</div>

      {/* ---- Section 1: Extend Existing Dataset ---- */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Extend Existing Dataset</div>
        {extendableDatasets.length === 0 && (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>No existing datasets to extend.</div>
        )}
        {extendableDatasets.map((ds) => {
          const st = extendState[ds.id] || {};
          const defaultStart = ds.year_end + 1;
          const defaultEnd = ds.availableMax;
          return (
            <div key={ds.id} style={styles.card}>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#f1f5f9' }}>
                  Dataset: {ds.sourceNames.join(' + ')}
                </span>
              </div>
              <div style={{ ...styles.row, marginBottom: 8 }}>
                <span style={styles.label}>
                  Current: {ds.year_start}{'\u2013'}{ds.year_end}
                </span>
                <span style={styles.label}>
                  Available: {ds.availableMin}{'\u2013'}{ds.availableMax}
                </span>
              </div>
              {ds.canExtend ? (
                <>
                  <div style={{ ...styles.row, marginBottom: 8 }}>
                    <label style={styles.label}>
                      Start Year
                      <input
                        type="number"
                        style={styles.input}
                        value={st.startYear ?? defaultStart}
                        min={ds.availableMin}
                        max={ds.availableMax}
                        onChange={(e) => handleExtendChange(ds.id, 'startYear', e.target.value)}
                      />
                    </label>
                    <label style={styles.label}>
                      End Year
                      <input
                        type="number"
                        style={styles.input}
                        value={st.endYear ?? defaultEnd}
                        min={ds.availableMin}
                        max={ds.availableMax}
                        onChange={(e) => handleExtendChange(ds.id, 'endYear', e.target.value)}
                      />
                    </label>
                    <button
                      style={st.busy ? styles.buttonDisabled : styles.button}
                      disabled={st.busy}
                      onClick={() => handleExtend(ds)}
                    >
                      {st.busy ? 'Extending...' : 'Extend Dataset'}
                    </button>
                  </div>
                  {st.msg && (
                    <div
                      style={st.msg.startsWith('Error') ? styles.errorText : styles.successText}
                    >
                      {st.msg}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: '#94a3b8', fontSize: 13 }}>
                  Dataset covers the full available range.
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ---- Section 2: Add New Source ---- */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Download From a Source</div>
        {downloadableSources.length === 0 && (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>
            No integrated sources cover this location.
          </div>
        )}
        {downloadableSources.map((source) => {
          const sel = newSourceSelections[source.id] || {
            variables: new Set(),
            startYear: '',
            endYear: '',
          };
          const isBusy = downloadingSource === source.id;
          const isFocused = focusSourceId === source.id;
          return (
            <div
              key={source.id}
              ref={isFocused ? focusRef : null}
              style={{
                ...styles.card,
                ...(isFocused ? { border: '1px solid #3b82f6', boxShadow: '0 0 0 1px #3b82f6' } : {}),
              }}
            >
              <div style={{ ...styles.row, marginBottom: 6 }}>
                <span style={styles.sourceColor(source.color)} />
                <span style={{ fontWeight: 600, fontSize: 14, color: '#f1f5f9' }}>
                  {source.name}
                </span>
                <span style={styles.tag}>{source.type}</span>
                {source.inUse && (
                  <span style={{ ...styles.tag, backgroundColor: '#14532d', color: '#86efac' }}>
                    already stored
                  </span>
                )}
                {source.availability_status === 'fallback' && (
                  <span style={{ ...styles.tag, backgroundColor: '#422006', color: '#fbbf24' }}>
                    years unverified
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>
                {source.provider}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>
                Years: {source.year_min || '?'}{'\u2013'}{source.year_max || '?'} | Distance:{' '}
                {source.distance_km} km
              </div>

              {/* Variables */}
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ ...styles.label, marginBottom: 0 }}>Select variables:</span>
                  <button style={styles.buttonSmall} onClick={() => selectAllVariables(source, true)}>
                    All
                  </button>
                  <button style={styles.buttonSmall} onClick={() => selectAllVariables(source, false)}>
                    None
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(source.variables || []).map((v) => (
                    <label
                      key={v}
                      style={{ fontSize: 13, color: '#cbd5e1', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    >
                      <input
                        type="checkbox"
                        style={styles.checkbox}
                        checked={sel.variables.has ? sel.variables.has(v) : false}
                        onChange={() => toggleNewSourceVariable(source.id, v)}
                      />
                      {v}
                    </label>
                  ))}
                </div>
              </div>

              {/* Year range */}
              <div style={{ ...styles.row, marginBottom: 8 }}>
                <label style={styles.label}>
                  Start Year
                  <input
                    type="number"
                    style={styles.input}
                    placeholder={source.year_min}
                    value={sel.startYear}
                    min={source.year_min}
                    max={source.year_max}
                    onChange={(e) =>
                      handleNewSourceYearChange(source.id, 'startYear', e.target.value)
                    }
                  />
                </label>
                <label style={styles.label}>
                  End Year
                  <input
                    type="number"
                    style={styles.input}
                    placeholder={source.year_max}
                    value={sel.endYear}
                    min={source.year_min}
                    max={source.year_max}
                    onChange={(e) =>
                      handleNewSourceYearChange(source.id, 'endYear', e.target.value)
                    }
                  />
                </label>
                <button
                  style={
                    isBusy || sel.variables.size === 0 ? styles.buttonDisabled : styles.button
                  }
                  disabled={isBusy || sel.variables.size === 0}
                  onClick={() => handleDownloadNewSource(source)}
                >
                  {isBusy ? 'Downloading...' : 'Download'}
                </button>
              </div>
              {downloadMsgSource === source.id && downloadMsg && (
                <div
                  style={downloadMsg.startsWith('Error') ? styles.errorText : styles.successText}
                >
                  {downloadMsg}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ---- Section 3: Variable Gaps ---- */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Variable Gaps</div>
        <div style={styles.card}>
          {CLIMATE_VARIABLES.map((cv) => {
            const isCovered = !!covered[cv.id];
            const providers = isCovered ? [] : sourcesForVariable(cv.id);
            const isAdding = gapBusy === cv.id;
            return (
              <div key={cv.id} style={styles.variableRow}>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  {isCovered ? (
                    <span style={styles.checkmark}>{'\u2713'}</span>
                  ) : (
                    <span style={styles.missing}>{'\u2717'}</span>
                  )}
                  <span style={{ fontSize: 13, color: isCovered ? '#e2e8f0' : '#f87171' }}>
                    {cv.label}
                  </span>
                  {isCovered && (
                    <span style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>
                      ({covered[cv.id]})
                    </span>
                  )}
                </div>
                {!isCovered && providers.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {providers.map((p) => (
                      <button
                        key={p.id}
                        style={isAdding ? styles.buttonDisabled : styles.buttonSmall}
                        disabled={isAdding}
                        onClick={() => handleAddVariable(cv.id, p.id)}
                        title={`Add ${cv.id} from ${p.name}`}
                      >
                        {isAdding ? 'Adding...' : `Add from ${p.name}`}
                      </button>
                    ))}
                  </div>
                )}
                {!isCovered && providers.length === 0 && (
                  <span style={{ fontSize: 11, color: '#64748b' }}>No source available</span>
                )}
              </div>
            );
          })}
          {gapMsg && (
            <div
              style={{
                ...(gapMsg.startsWith('Error') ? styles.errorText : styles.successText),
                marginTop: 8,
              }}
            >
              {gapMsg}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddDataPanel;
